import { supabase } from './supabaseService';

export interface LeadScore {
  id: string;
  lead_id: string;
  score: number;
  classification: string;
  risk_flags: string[];
  factors: Record<string, number>;
  budget_signals: string[];
  source: string;
  created_at: string;
}

export interface PendingEmail {
  id: string;
  lead_id: string;
  action_type: string;
  recipient: string;
  subject: string;
  body: string;
  score: number;
  classification: string;
  risk_flags: string[];
  status: string;
  created_at: string;
}

export interface EmailLog {
  id: string;
  lead_id: string;
  recipient: string;
  subject: string;
  send_status: string;
  delivered_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  bounced_at: string | null;
  open_count: number;
  click_count: number;
  created_at: string;
}

export interface ActionLog {
  id: string;
  lead_id: string;
  action_type: string;
  reason: string;
  status: string;
  mode: string;
  metadata: Record<string, any>;
  created_at: string;
}

export interface CommercialMetrics {
  totalLeads: number;
  distribution: Record<string, number>;
  emailsSent7d: number;
  emailsSent30d: number;
  opens30d: number;
  clicks30d: number;
  bounces30d: number;
  openRate: number;
  pendingApproval: number;
  pipelineValueEur: number;
}

export const commercialService = {
  async getLatestScores(days: number = 30): Promise<LeadScore[]> {
    const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
    const { data } = await supabase
      .from('lead_scores')
      .select('*')
      .gte('created_at', cutoff)
      .order('created_at', { ascending: false });
    return data || [];
  },

  async getPendingEmails(): Promise<PendingEmail[]> {
    const { data } = await supabase
      .from('pending_emails')
      .select('*')
      .in('status', ['pending', 'editing'])
      .order('created_at', { ascending: false });
    return data || [];
  },

  async getEmailLogs(days: number = 30): Promise<EmailLog[]> {
    const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
    const { data } = await supabase
      .from('email_logs')
      .select('*')
      .gte('created_at', cutoff)
      .order('created_at', { ascending: false })
      .limit(50);
    return data || [];
  },

  async getActionLogs(days: number = 7): Promise<ActionLog[]> {
    const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
    const { data } = await supabase
      .from('action_logs')
      .select('*')
      .gte('created_at', cutoff)
      .order('created_at', { ascending: false })
      .limit(100);
    return data || [];
  },

  async getMetrics(): Promise<CommercialMetrics> {
    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();

    const scores = await this.getLatestScores(30);

    // Deduplicate: latest per lead
    const latest = new Map<string, LeadScore>();
    for (const s of scores) {
      if (!latest.has(s.lead_id)) latest.set(s.lead_id, s);
    }
    const uniqueScores = Array.from(latest.values());

    const distribution: Record<string, number> = { PRIORIDAD_ALTA: 0, INTERESANTE: 0, DUDOSO: 0, DESCARTAR: 0 };
    let pipelineValue = 0;
    for (const s of uniqueScores) {
      if (s.classification in distribution) distribution[s.classification]++;
      if (s.classification === 'PRIORIDAD_ALTA' || s.classification === 'INTERESANTE') {
        for (const bs of s.budget_signals || []) {
          const match = String(bs).match(/(\d+)/);
          if (match) { pipelineValue += parseInt(match[1]); break; }
        }
      }
    }

    const { count: sent7d } = await supabase.from('email_logs').select('id', { count: 'exact' }).eq('send_status', 'sent').gte('created_at', sevenDaysAgo);
    const { count: sent30d } = await supabase.from('email_logs').select('id', { count: 'exact' }).eq('send_status', 'sent').gte('created_at', thirtyDaysAgo);
    const { count: opens } = await supabase.from('email_events').select('id', { count: 'exact' }).eq('event_type', 'opened').gte('created_at', thirtyDaysAgo);
    const { count: clicks } = await supabase.from('email_events').select('id', { count: 'exact' }).eq('event_type', 'clicked').gte('created_at', thirtyDaysAgo);
    const { count: bounces } = await supabase.from('email_events').select('id', { count: 'exact' }).eq('event_type', 'bounced').gte('created_at', thirtyDaysAgo);
    const pending = await this.getPendingEmails();

    return {
      totalLeads: uniqueScores.length,
      distribution,
      emailsSent7d: sent7d || 0,
      emailsSent30d: sent30d || 0,
      opens30d: opens || 0,
      clicks30d: clicks || 0,
      bounces30d: bounces || 0,
      openRate: sent30d ? Math.round(((opens || 0) / sent30d) * 100) : 0,
      pendingApproval: pending.length,
      pipelineValueEur: pipelineValue,
    };
  },
};
